/**
 * InjuryPushWatcher — headless component.
 *
 * Polls /api/injuries/events every 60 seconds. When a new event arrives where
 * curr_status ∈ {OUT, DOUBTFUL, IR, PUP}, fires:
 *   - Browser Notification (if permission granted)
 *   - Sonner toast
 *   - Custom event `wp:injury-event` (LateInjuryModal listens to auto-populate)
 *
 * Only polls when the current user is authenticated. Uses `only_slate=true`
 * so alerts are filtered to players actually in the user's DK pool.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const POLL_MS = 60_000;
const SEVERE_STATUSES = new Set(["OUT", "IR", "PUP", "DOUBTFUL"]);
const SINCE_KEY = "wp_injury_last_seen";

export default function InjuryPushWatcher({ enabled = true }) {
  const timerRef = useRef(null);
  const dedupeRef = useRef(new Set());

  useEffect(() => {
    if (!enabled) return;

    const poll = async () => {
      try {
        const since = localStorage.getItem(SINCE_KEY) || null;
        const r = await api.injuryEvents(since, true);
        const events = r.events || [];
        if (events.length > 0) {
          for (const e of events) {
            const key = `${e.key}::${e.at}`;
            if (dedupeRef.current.has(key)) continue;
            dedupeRef.current.add(key);
            const curr = (e.curr_status || "").toUpperCase();
            if (!SEVERE_STATUSES.has(curr)) continue;
            fireAlert(e);
          }
          // Save the newest event timestamp
          const newest = events.reduce((m, e) => (e.at > m ? e.at : m), since || "");
          if (newest) localStorage.setItem(SINCE_KEY, newest);
        }
        if (r.server_time) localStorage.setItem(SINCE_KEY, r.server_time);
      } catch {
        // Silent — this is a background poll, don't spam the user with errors
      }
    };

    // First poll after 3 seconds; then every 60s
    const initial = setTimeout(poll, 3000);
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      clearTimeout(initial);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled]);

  return null;
}

function fireAlert(event) {
  const player = event.player_name || "Player";
  const team = event.team || "";
  const pos = event.position || "";
  const from = event.prev_status || "?";
  const to = event.curr_status || "?";
  const label = `${player} (${team} ${pos}) — ${to}`;
  const desc = from !== "ACTIVE" ? `Status changed: ${from} → ${to}` : `Just added to injury report: ${to}`;

  // Toast
  toast.warning(label, {
    id: `injury-${event.key}`,
    description: desc,
    duration: 12000,
  });

  // Browser Notification
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      new Notification("WarPaint DFS — Injury Alert", {
        body: `${label}\n${desc}`,
        tag: `injury-${event.key}`,
        icon: "/brand/helmet_transparent.png",
        requireInteraction: true,
      });
    } catch {
      // ignore
    }
  }

  // Custom event so LateInjuryModal / other components can auto-populate
  try {
    window.dispatchEvent(new CustomEvent("wp:injury-event", { detail: event }));
  } catch {
    // ignore
  }
}
